import { cx } from "shared/utils/classnames";
import { ChevronLeft, CircleHelp, Settings, XIcon } from "lucide-react";
import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { Button } from "shared/components/Button";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { useAuth } from "providers/auth";
import { postEvent } from "helpers/postEvent";
import { usePostHog } from "posthog-js/react";
import { Hide } from "shared/components/helpers/Hide";
import { Show } from "shared/components/helpers/Show";

export const BenefitLayout = ({
  children,
  title,
  hideFooter,
  mainClassName,
  style,
  hasBack,
  titleClassName,
  footer,
  footerClassName,
  banner,
  hasTabView
}: {
  children?: ReactNode;
  title: string;
  titleClassName?: string;
  hideFooter?: boolean;
  mainClassName?: string;
  style?: React.CSSProperties;
  hasBack?: boolean;
  footer?: ReactNode;
  footerClassName?: string;
  banner?: ReactNode;
  hasTabView?: boolean;
}) => {
  const intercom = useIntercom();
  const { sdkConfig } = useAuth();
  const { pathname } = useLocation();

  const { featureFlags } = usePostHog();

  const hideSupportSettings =
    (Boolean(featureFlags?.isFeatureEnabled("hide_support_settings")) &&
      pathname === "/settings") ||
    Boolean(featureFlags?.isFeatureEnabled("hide_support"));

  // If there is not hub page, hide the back button
  const isRentReportingIdPage = /^\/rent_reporting\/(current|\d+)(\/?)$/;
  const hideBackToHome =
    Boolean(featureFlags?.isFeatureEnabled("hide_hub_page")) &&
    isRentReportingIdPage.test(pathname);

  return (
    <div className="flex flex-col h-screen">
      <div
        className={
          hasTabView
            ? "border-b border-alpha-100"
            : hasBack && !banner
              ? "md:border-b border-alpha-100"
              : ""
        }
      >
        <nav className="py-4 pr-1 pl-4 flex items-center justify-between h-16">
          {hasBack ? (
            <Show if={!hideBackToHome}>
              <Link
                to={
                  location.pathname.includes("settings") &&
                  location.pathname !== "/settings"
                    ? "/settings"
                    : "/hub"
                }
              >
                <IconButton
                  className="md:hidden"
                  Icon={ChevronLeft}
                  size={IconButtonSize.LG}
                  borderLess
                />
              </Link>
            </Show>
          ) : (
            <h1
              className={cx(
                "text-2xl text-content-1000 font-bold",
                titleClassName
              )}
            >
              {title}
            </h1>
          )}

          <div className="ml-auto flex gap-1 items-center">
            <Hide if={hideSupportSettings}>
              <IconButton
                Icon={CircleHelp}
                size={IconButtonSize.LG}
                borderLess
                onClick={() => {
                  intercom.show();
                }}
              />
            </Hide>
            <Show if={pathname === "/hub"}>
              <Link to={"/settings"} className="h-[56px]">
                <IconButton
                  Icon={Settings}
                  size={IconButtonSize.LG}
                  borderLess
                />
              </Link>
            </Show>

            <Show if={sdkConfig?.hasClose}>
              <IconButton
                Icon={XIcon}
                size={IconButtonSize.LG}
                borderLess
                onClick={() => {
                  postEvent({
                    type: "action",
                    action: "close"
                  });
                }}
              />
            </Show>
          </div>
        </nav>
        {banner ? banner : null}
      </div>

      <main
        className={cx(
          mainClassName,
          "flex-1 h-full overflow-y-auto hideScrollbar px-4"
        )}
        style={style}
      >
        <Show if={hasBack}>
          <h1
            className={cx(
              "text-2xl text-content-1000 font-bold mb-4",
              titleClassName
            )}
          >
            {title}
          </h1>
        </Show>
        {children}
      </main>
      <Show if={(!hideFooter || hasBack) && !hideBackToHome}>
        <footer
          className={cx(
            "border-t border-alpha-100 p-4",
            footerClassName,
            footer ? "" : "hidden md:block"
          )}
        >
          {footer ? (
            footer
          ) : (
            <Link
              to={
                location.pathname.includes("settings") &&
                location.pathname !== "/settings"
                  ? "/settings"
                  : "/hub"
              }
            >
              <Button>Back</Button>
            </Link>
          )}
        </footer>
      </Show>
    </div>
  );
};
